import { FC, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import Transition from 'react-transition-group/Transition';
import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { SelectionValue } from '~src/interfaces/question.interface';
import CardNavigation from '~src/modules/questions/components/CardNavigation';
import LoadingCard from '~src/modules/questions/components/cards/LoadingCard';
import SelectionCard from '~src/modules/questions/components/cards/SelectionCard';
import MobileTongue from '~src/modules/questions/components/MobileTongue';
import PopoverInfo from '~src/modules/questions/components/PopoverInfo';
import QuestionsInfo from '~src/modules/questions/components/QuestionsInfo';
import { NavigationContainer } from '~src/modules/questions/components/shared.sc';
import { SelectionType } from '~src/services/api/pulseCheck';
import { defaultPulseCheckApi } from '~src/services/ApiController';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import { Mobile } from '~src/styles/containers.sc';

import { QuestionsGrid, QuestionsWrapper } from './questions.sc';
import { animationSpeed, CardAnimateur } from './stream.sc';

interface OwnProps {
  finishQS: (pack: PackName) => void;
  cycle: CycleI;
  precedent?: string;
}

export function determineCurrentCardIndex(managersSelection: SelectionType[], precedent?: string): number {
  const idx = managersSelection.findIndex((selectionType) => {
    if (precedent) {
      return selectionType.about.id === precedent;
    }

    return !selectionType.asked || !selectionType.asked.length;
  });

  return idx === -1 ? 0 : idx;
}

const ManagerSelection: FC<OwnProps> = (props) => {
  const [wantCardShown, setWantCardShown] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [managersSelection, setManagersSelection] = useState<SelectionType[]>([]);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [animationDirection, setAnimationDirection] = useState<'left'|'right'>('left');

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    defaultPulseCheckApi.withSignal(controller.signal).getManagersSelection(props.cycle.id, props.precedent).then((response) => {
      setManagersSelection(response.data);
      setIsLoading(false);
      setWantCardShown(true);
    }).catch((e) => {throw e});

    return () => {
      controller.abort('ManagerSelection unmounted');
    };
  }, [props.cycle.id]);


  const getCurrentSelection = (): SelectionType => {
    const pack = managersSelection;
    let index = pack.length - 1;
    if (currentCardIndex < pack.length) {
      index = currentCardIndex;
    }
    return pack[index];
  };

  const sendAnswer = (value: SelectionValue) => {
    const current = getCurrentSelection();
    const postData = {
      about_id: current.about.id,
      ask_ids: value.selectedPeople.map((val) => val.id),
      cycle_id: props.cycle.id,
    };

    setIsSaveLoading(true);
    defaultPulseCheckApi.postManagersSelection(postData)
      .then(() => {
        managersSelection[currentCardIndex].asked = value.selectedPeople;
        nextCard();
      })
      .catch((error: Error) => {
        logger.warn('Error updating ManagersSelection', {
          error,
          info: {
            componentStack: 'Questions.tsx',
          },
        });
        throw error;
      }).finally(() => {
        setIsSaveLoading(false);
      });
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setAnimationDirection('right');

      moveCard(() => {
        setCurrentCardIndex(currentCardIndex - 1);
      });
    }
  };

  const moveCard = (callback: () => void) => {
    setWantCardShown(false);
    setTimeout(() => {
      callback();
      setWantCardShown(true);

    }, animationSpeed);
  };

  /* Check if card is last card and finish the stream after one last animation */
  const nextCard = () => {
    if (currentCardIndex >= managersSelection.length - 1) {
      setWantCardShown(false);

      setTimeout(() => {
        props.finishQS(PackName.ManagersSelection);
      }, animationSpeed);
    } else {
      setAnimationDirection('left');

      moveCard(() => {
        setCurrentCardIndex(currentCardIndex + 1);
      });
    }
  };

  const current = getCurrentSelection();
  const handlers = useSwipeable({
    onSwipedRight: () => prevCard,
    onSwipedLeft: () => nextCard,
  });

  return (
    <>
      <QuestionsWrapper>
        <QuestionsGrid>
          {isLoading ? (
            <LoadingCard />
          ) : (
            <>
              <Mobile>
                <PopoverInfo selection={PackName.ManagersSelection} />
              </Mobile>
              <Transition in={wantCardShown} timeout={animationSpeed}>
                {(status) => (
                  <CardAnimateur $direction={animationDirection} className={status}>
                    <div {...handlers}>
                      <SelectionCard
                        selectionType={PackName.ManagersSelection}
                        currentCard={current}
                        index={currentCardIndex}
                        onSubmit={sendAnswer}
                        saveLoading={isSaveLoading}
                      />
                    </div>
                  </CardAnimateur>
                )}
              </Transition>
              <NavigationContainer>
                <CardNavigation
                  back={prevCard}
                  next={nextCard}
                  max={managersSelection.length + 1}
                  value={currentCardIndex + 1}
                />
              </NavigationContainer>
              <QuestionsInfo
                count={managersSelection.length}
                dueDate={props.cycle.dueDate}
                selection={PackName.ManagersSelection}
                surveyName={translateContent(props.cycle.survey.name)}
              />
            </>
          )}
        </QuestionsGrid>
      </QuestionsWrapper>
      {!isLoading && (
        <MobileTongue
          name={translateContent(props.cycle.survey.name)}
          surveyLength={managersSelection.length}
          dueDate={props.cycle.dueDate}
        />
      )}
    </>
  );
}

export default ManagerSelection;
